import { useNavigate } from "react-router-dom";
import { ownerAlertPath } from "@/lib/notificationRoutes";
import { Bell, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NotificationItem } from "./NotificationItem";
import { isToday, isThisWeek } from "date-fns";
import type { Notification } from "@/hooks/useNotifications";

interface NotificationPanelProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onClearOne?: (id: string) => void;
  onClearAllRead: () => void;
  isClearingRead: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onClose: () => void;
}

function groupNotifications(notifications: Notification[]) {
  const today: Notification[] = [];
  const thisWeek: Notification[] = [];
  const earlier: Notification[] = [];

  for (const n of notifications) {
    const date = new Date(n.created_at);
    if (isToday(date)) today.push(n);
    else if (isThisWeek(date, { weekStartsOn: 1 })) thisWeek.push(n);
    else earlier.push(n);
  }

  return { today, thisWeek, earlier };
}

function GroupHeader({ label }: { label: string }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 px-3 pt-3 pb-1">
      {label}
    </p>
  );
}

export function NotificationPanel({
  notifications,
  onMarkAsRead,
  onClearOne,
  onClearAllRead,
  isClearingRead,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onClose,
}: NotificationPanelProps) {
  const navigate = useNavigate();
  const readCount = notifications.filter((n) => n.is_read).length;

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) onMarkAsRead(notification.id);

    const data = notification.data as Record<string, string>;
    const ownerPath = ownerAlertPath(notification.type, data);
    if (ownerPath) { navigate(ownerPath); return; }
    switch (notification.type) {
      case "badge_earned":
        navigate(data?.badge_slug ? `/profile?tab=achievements&badge=${data.badge_slug}` : "/profile?tab=achievements");
        break;
      case "review_liked":
      case "photo_liked":
      case "grounded_post_liked":
      case "check_in_liked":
      case "new_cafe_nearby":
      case "ownership_claim_approved":
      case "ownership_claim_denied":
      case "submission_approved":
        if (data?.cafe_slug) navigate(`/cafe/${data.cafe_slug}`);
        break;
      case "follow":
        if (data?.follower_id) navigate(`/user/${data.follower_id}`);
        break;
      case "wave":
        if (data?.from_user_id) navigate(`/user/${data.from_user_id}`);
        break;
      case "friend_request":
      case "friend_accepted":
        navigate(data?.from_user_id ? `/user/${data.from_user_id}` : data?.accepter_id ? `/user/${data.accepter_id}` : "/profile?tab=friends");
        break;
      case "weekly_recap":
      case "streak_milestone":
        navigate("/profile");
        break;
      case "friends_activity_nudge":
        navigate("/feed");
        break;
      case "content_report":
        navigate("/admin?tab=reports");
        break;
      case "ownership_claim":
        navigate("/admin?tab=claims");
        break;
      case "admin_signup":
        navigate("/admin?tab=users");
        break;
      case "admin_feedback":
        navigate("/admin?tab=feedback");
        break;
      case "admin_submission":
        navigate(data?.submission_id ? `/admin?tab=submissions&submissionId=${data.submission_id}` : "/admin?tab=submissions");
        break;
      case "admin_review":
        navigate(data?.cafe_slug ? `/cafe/${data.cafe_slug}` : "/admin?tab=reviews");
        break;
      case "admin_checkin":
        navigate(data?.cafe_slug ? `/cafe/${data.cafe_slug}` : "/admin?tab=engagement");
        break;
      case "admin_owner_description":
        navigate("/admin?tab=owner-edits");
        break;
      case "admin_photo":
        navigate(data?.cafe_slug ? `/cafe/${data.cafe_slug}` : "/admin?tab=photos");
        break;
      default:
        break;
    }

    onClose();
  };

  const { today, thisWeek, earlier } = groupNotifications(notifications);

  return (
    <div className="flex flex-col h-full max-h-[inherit] min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 shrink-0">
        <h3 className="font-semibold text-sm">Notifications</h3>
        {readCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAllRead}
            disabled={isClearingRead}
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            Clear {readCount} read
          </Button>
        )}
      </div>

      {/* Content */}
      {notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 py-12 px-4 text-center">
          <div className="p-3 rounded-full bg-secondary mb-3">
            <Bell className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium">All caught up</p>
          <p className="text-xs text-muted-foreground mt-1">Nothing new right now</p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <div className="p-2 pb-4">
            {today.length > 0 && (
              <>
                <GroupHeader label="Today" />
                {today.map((n) => (
                  <NotificationItem key={n.id} notification={n} onClick={() => handleNotificationClick(n)} onClear={onClearOne ? () => onClearOne(n.id) : undefined} />
                ))}
              </>
            )}
            {thisWeek.length > 0 && (
              <>
                <GroupHeader label="This week" />
                {thisWeek.map((n) => (
                  <NotificationItem key={n.id} notification={n} onClick={() => handleNotificationClick(n)} onClear={onClearOne ? () => onClearOne(n.id) : undefined} />
                ))}
              </>
            )}
            {earlier.length > 0 && (
              <>
                <GroupHeader label="Earlier" />
                {earlier.map((n) => (
                  <NotificationItem key={n.id} notification={n} onClick={() => handleNotificationClick(n)} onClear={onClearOne ? () => onClearOne(n.id) : undefined} />
                ))}
              </>
            )}

            {hasNextPage && (
              <div className="pt-2 px-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-muted-foreground"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLoadMore(); }}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? (
                    <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Loading...</>
                  ) : (
                    "Load older notifications"
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
